import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChangePasswordInput,
  ForgotPasswordInput,
  LoginRequest,
  RegisterInput,
  ResetPasswordInput,
  SessionUserDto,
  SignAgreementInput,
  UpdateProfileInput,
} from '@shared/contracts/auth';
import { endpoints } from '../../api/endpoints';
import { qk } from '../../api/queryKeys';
import { ApiError, getErrorMessage } from '../../lib/apiClient';
import { PATHS } from '../../routes/paths';
import { toast } from '../../stores/toastStore';

/**
 * Oturum durumu.
 * Kullanıcı bilgisi de sunucu state'idir — Zustand'a kopyalanmaz.
 */
export function useSession() {
  const query = useQuery({
    queryKey: qk.me(),
    queryFn: endpoints.me,
    staleTime: 5 * 60_000,
    // 401 beklenen bir durumdur (giriş yapılmamış); tekrar denemenin anlamı yok.
    retry: (failureCount, error) =>
      error instanceof ApiError && error.isRetryable && failureCount < 2,
  });

  const isUnauthenticated = query.error instanceof ApiError && query.error.status === 401;

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isAuthenticated: Boolean(query.data),
    isUnauthenticated,
    error: query.error,
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginRequest) => endpoints.login(input),
    onSuccess: (user) => {
      qc.setQueryData(qk.me(), user);
      // Rol değişti → önceki oturumun önbelleği tamamen geçersiz.
      void qc.invalidateQueries();
      toast.success(`Hoş geldiniz, ${user.name}.`);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegisterInput) => endpoints.register(input),
    onSuccess: (user) => {
      qc.setQueryData(qk.me(), user);
      toast.success('Hesabınız oluşturuldu.');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: endpoints.logout,
    onSettled: () => {
      // Başarısız olsa bile yerel önbellek temizlenir.
      qc.clear();

      /* `clear()` TEK BAŞINA yetmiyor: önbelleği boşaltır ama bağlı
         gözlemcileri yeniden getirmeye zorlamaz; ekranda ÖNCEKİ kullanıcının
         verisi kalır ve kullanıcı çıkış yapamadığını sanır. E2E paketi bunu
         ilk çalıştırmada yakaladı.

         Tam yeniden yükleme, çıkışın vermesi gereken garantiyi kesinleştirir:
         önceki kullanıcıya ait hiçbir şey — sorgu önbelleği, bileşen state'i,
         Zustand store'u — bellekte kalmaz. Çıkış nadir bir işlem olduğu için
         maliyeti de önemsiz. */
      window.location.assign('/');
    },
  });
}

/**
 * Şifre değiştirme.
 *
 * Sunucu `tokenVersion`'ı artırır (diğer TÜM oturumlar düşer) ama BU oturuma
 * yeni sürümle taze çerez basar — rotasyonu tamamlayan kullanıcı yeniden giriş
 * yapmaz. Tam yeniden yükleme yine de gerekli: önbellekteki her şey eski
 * oturumla alınmıştı ve rotasyon kilidi açıldığına göre gidilecek yer panel.
 */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => endpoints.changePassword(input),
    onSuccess: (_data, _input, _context) => {
      qc.clear();
      window.location.assign(PATHS.PANEL);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * "Şifremi unuttum" — e-posta gönderme isteği.
 *
 * Başarı/başarısızlık ayrımı yok: uç kullanıcı sızdırmadığı için e-posta
 * kayıtlı olsun olmasın 200 döner. Bileşen `isSuccess` ile "gönderildi"
 * ekranını çiziyor; hata yalnızca ağ/hız-sınırı durumunda toast'a düşer. */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (input: ForgotPasswordInput) => endpoints.forgotPassword(input),
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** E-postadaki jetonla yeni şifre belirleme; başarıda giriş ekranına döner. */
export function useResetPassword() {
  return useMutation({
    mutationFn: (input: ResetPasswordInput) => endpoints.resetPassword(input),
    onSuccess: () => {
      toast.success('Şifreniz güncellendi. Yeni şifrenizle giriş yapabilirsiniz.');
      window.location.assign(PATHS.AUTH);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => endpoints.updateProfile(input),
    onSuccess: (user: SessionUserDto) => {
      qc.setQueryData(qk.me(), user);
      toast.success('Sağlık notlarınız ve hedefiniz kaydedildi.');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Üyelik sözleşmesi imzası. Sunucu güncel oturumu döndürür
 * (`agreementRequired: false`); kapı `App.tsx`'te o bayrağa bakıyor.
 * Önbellek tazelenir: kapı açılmadan önce alınan her şey 403'tü.
 */
export function useSignAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignAgreementInput) => endpoints.signAgreement(input),
    onSuccess: (user) => {
      qc.setQueryData(qk.me(), user);
      void qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
      toast.success('Sözleşme onaylandı. Hoş geldiniz!');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** En son sözleşme imzası (Profilim). */
export function useMyAgreement({ enabled = true } = {}) {
  return useQuery({
    queryKey: qk.myAgreement(),
    queryFn: endpoints.myAgreement,
    staleTime: 5 * 60_000,
    enabled,
  });
}
